import React, { useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { Menu, X, Facebook, Instagram } from "lucide-react";

const links = [
  { to: "/", label: "Home" },
  { to: "/products", label: "Products" },
  { to: "/categories", label: "Categories" },
  { to: "/about", label: "About" },
  { to: "/dealer", label: "Become a Dealer" },
  { to: "/contact", label: "Contact" },
];

const SOCIAL = {
  facebook: "https://www.facebook.com/share/1NfGTfqoUc/",
  instagram: "https://www.instagram.com/arel.nepal/",
};

const Navbar = () => {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  return (
    <header
      className="arel-glass sticky top-0 z-50 w-full"
      data-testid="site-navbar"
    >
      <div className="mx-auto flex h-24 max-w-7xl items-center justify-between px-6 lg:px-10">
        <Link
          to="/"
          className="flex items-center"
          data-testid="nav-logo-link"
          onClick={() => setOpen(false)}
        >
          <img
            src="/assets/logo/arel-logo.png"
            alt="AREL Nepal — Energizing Life"
            className="h-14 md:h-16 w-auto"
          />
        </Link>

        <nav className="hidden items-center gap-6 lg:flex">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.to === "/"}
              data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) =>
                `text-sm font-semibold tracking-wide transition-colors duration-300 ease-out ${
                  isActive
                    ? "text-[#dd0447]"
                    : "text-[#1e1e22] hover:text-[#dd0447]"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
          <NavLink
            to="/daikin"
            data-testid="nav-link-daikin"
            className={({ isActive }) =>
              `inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-sm font-semibold transition-colors duration-300 ease-out ${
                isActive
                  ? "border-[#0097e0] bg-[#e6f6fe] text-[#0097e0]"
                  : "border-[#0097e0]/40 text-[#0097e0] hover:border-[#0097e0] hover:bg-[#e6f6fe]"
              }`
            }
          >
            <img
              src="/assets/logo/daikin-logo.png"
              alt="Daikin"
              className="h-5 w-auto"
            />
            <span>Daikin ACs</span>
          </NavLink>
        </nav>

        <div className="hidden items-center gap-3 lg:flex">
          <a
            href={SOCIAL.facebook}
            target="_blank"
            rel="noreferrer"
            data-testid="nav-social-facebook"
            className="flex h-9 w-9 items-center justify-center rounded-full border border-line-strong text-[#575761] transition-colors hover:border-[#dd0447] hover:text-[#dd0447]"
            aria-label="Facebook"
          >
            <Facebook className="h-4 w-4" />
          </a>
          <a
            href={SOCIAL.instagram}
            target="_blank"
            rel="noreferrer"
            data-testid="nav-social-instagram"
            className="flex h-9 w-9 items-center justify-center rounded-full border border-line-strong text-[#575761] transition-colors hover:border-[#dd0447] hover:text-[#dd0447]"
            aria-label="Instagram"
          >
            <Instagram className="h-4 w-4" />
          </a>
          <button
            data-testid="nav-inquire-btn"
            onClick={() => navigate("/contact")}
            className="arel-primary-btn rounded-full px-5 py-2.5 text-sm font-semibold"
          >
            Inquire Now
          </button>
        </div>

        <button
          className="lg:hidden text-[#0b0b0d] p-2"
          onClick={() => setOpen((o) => !o)}
          data-testid="nav-mobile-toggle"
          aria-label="Toggle menu"
        >
          {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </div>

      {open && (
        <div
          className="border-t border-line bg-[#fbf9f6] lg:hidden"
          data-testid="nav-mobile-menu"
        >
          <div className="mx-auto flex max-w-7xl flex-col px-6 py-4">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.to === "/"}
                onClick={() => setOpen(false)}
                data-testid={`nav-mobile-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={({ isActive }) =>
                  `py-3 text-base font-medium ${
                    isActive ? "text-[#dd0447]" : "text-[#1e1e22]"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
            <NavLink
              to="/daikin"
              onClick={() => setOpen(false)}
              data-testid="nav-mobile-link-daikin"
              className={({ isActive }) =>
                `mt-2 inline-flex items-center gap-2 rounded-full border px-3 py-2 text-sm font-semibold ${
                  isActive
                    ? "border-[#0097e0] bg-[#e6f6fe] text-[#0097e0]"
                    : "border-[#0097e0]/40 text-[#0097e0]"
                }`
              }
            >
              <img
                src="/assets/logo/daikin-logo.png"
                alt="Daikin"
                className="h-4 w-auto"
              />
              Daikin ACs
            </NavLink>
            <div className="mt-4 flex items-center gap-3">
              <a href={SOCIAL.facebook} target="_blank" rel="noreferrer" className="flex h-9 w-9 items-center justify-center rounded-full border border-line-strong text-[#575761]">
                <Facebook className="h-4 w-4" />
              </a>
              <a href={SOCIAL.instagram} target="_blank" rel="noreferrer" className="flex h-9 w-9 items-center justify-center rounded-full border border-line-strong text-[#575761]">
                <Instagram className="h-4 w-4" />
              </a>
            </div>
            <button
              data-testid="nav-mobile-inquire-btn"
              onClick={() => {
                setOpen(false);
                navigate("/contact");
              }}
              className="arel-primary-btn mt-3 rounded-full px-5 py-3 text-sm font-semibold"
            >
              Inquire Now
            </button>
          </div>
        </div>
      )}
    </header>
  );
};

export default Navbar;
